'use client';

import type { GooglePlaceDto } from '@up2stay/up2promo-api-client';
import { Label, Textarea } from '@up2stay/up2promo-ui';
import { MapPin } from 'lucide-react';

import type { LocationAddressDetails } from '@/core/components/locations/location-details-form';
import { addressDetailsFromPlace } from '@/core/components/locations/location-address-details';
import { LocationMapPreview } from '@/core/components/locations/location-map-preview';
import { LocationSearchCombobox } from '@/core/components/locations/location-search-combobox';
import { SettingsInfoHint } from '@/core/components/settings/settings-info-hint';

export type LocationAddressEditorLabels = {
  searchLabel?: string;
  searchPlaceholder?: string;
  searchHint?: string;
  /** Label for the selected address (editable). */
  addressLabel?: string;
  selectedEmptyLabel?: string;
  addressEditHint?: string;
  mapPreviewLabel?: string;
  noCoordinatesLabel?: string;
  mapUnavailableLabel?: string;
  dragHintLabel?: string;
};

export function LocationAddressEditor({
  addressDetails,
  onAddressDetailsChange,
  latitude,
  longitude,
  onCoordinatesChange,
  onPlaceSelect,
  name,
  disabled = false,
  labels,
  compactMap = false,
}: {
  addressDetails: LocationAddressDetails | null;
  onAddressDetailsChange: (details: LocationAddressDetails) => void;
  latitude: number | undefined;
  longitude: number | undefined;
  onCoordinatesChange: (
    latitude: number | undefined,
    longitude: number | undefined,
  ) => void;
  /**
   * Called after address + coords are applied from place search.
   * Location create may persist `place.id` as the Google listing ID.
   * Location detail address search must not change the listing.
   */
  onPlaceSelect?: (place: GooglePlaceDto) => void;
  name?: string;
  disabled?: boolean;
  labels?: LocationAddressEditorLabels;
  compactMap?: boolean;
}) {
  const details = addressDetails ?? {};
  const formatted = details.formatted ?? '';
  const hasCoords =
    latitude !== undefined &&
    longitude !== undefined &&
    !Number.isNaN(latitude) &&
    !Number.isNaN(longitude);

  const handlePlaceSelect = (place: GooglePlaceDto) => {
    onAddressDetailsChange(addressDetailsFromPlace(place));
    if (place.latitude != null && place.longitude != null) {
      onCoordinatesChange(place.latitude, place.longitude);
    }
    onPlaceSelect?.(place);
  };

  const searchLabel = labels?.searchLabel ?? 'Search';
  const addressLabel = labels?.addressLabel ?? 'Address';

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-0.5">
          {labels?.searchLabel ? (
            <Label htmlFor="location-address-search">
              {labels.searchLabel}
            </Label>
          ) : null}
          {labels?.searchHint ? (
            <SettingsInfoHint label={searchLabel}>
              {labels.searchHint}
            </SettingsInfoHint>
          ) : null}
        </div>
        <LocationSearchCombobox
          id="location-address-search"
          aria-label={labels?.searchLabel ? undefined : searchLabel}
          placeholder={labels?.searchPlaceholder}
          onSelect={handlePlaceSelect}
          disabled={disabled}
        />
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-0.5">
          {labels?.addressLabel ? (
            <Label htmlFor="location-formatted-address">
              {labels.addressLabel}
            </Label>
          ) : null}
          {labels?.addressEditHint ? (
            <SettingsInfoHint label={addressLabel}>
              {labels.addressEditHint}
            </SettingsInfoHint>
          ) : null}
        </div>
        <Textarea
          id="location-formatted-address"
          value={formatted}
          placeholder={labels?.selectedEmptyLabel}
          disabled={disabled}
          rows={2}
          className="min-h-16 resize-none"
          aria-label={labels?.addressLabel}
          onChange={(event) =>
            onAddressDetailsChange({
              ...details,
              formatted: event.target.value,
            })
          }
        />
      </div>

      {hasCoords ? (
        <LocationMapPreview
          latitude={latitude}
          longitude={longitude}
          name={name}
          mapPreviewLabel={labels?.mapPreviewLabel}
          noCoordinatesLabel={labels?.noCoordinatesLabel}
          mapUnavailableLabel={labels?.mapUnavailableLabel}
          dragHintLabel={labels?.dragHintLabel}
          compact={compactMap}
          bare
          interactive
          onPositionChange={(lat, lng) => onCoordinatesChange(lat, lng)}
        />
      ) : (
        <div className="flex flex-col items-center justify-center gap-2 rounded-[var(--r-settings-group,12px)] bg-muted/30 px-4 py-8">
          <MapPin className="size-8 text-muted-foreground" aria-hidden />
          <p className="text-[12px] leading-snug text-muted-foreground/85 text-pretty">
            {labels?.noCoordinatesLabel}
          </p>
        </div>
      )}
    </div>
  );
}
